/**
 * VIP等级徽章组件
 * 显示用户的VIP等级
 */

import { VIPLevel } from '../../types/vip.js';
import type { VIPLevelCode } from '../../types/vip.js';

interface VIPBadgeProps {
  level: VIPLevel;
  code?: VIPLevelCode;
  size?: 'small' | 'medium' | 'large';
  showText?: boolean;
  className?: string;
}

/**
 * VIP等级配置
 */
const VIP_CONFIGS: Record<VIPLevelCode, { color: string; bgColor: string; icon: string }> = {
  free: {
    color: '#6B7280',
    bgColor: '#F3F4F6',
    icon: '👤',
  },
  vip: {
    color: '#D97706',
    bgColor: '#FEF3C7',
    icon: '⭐',
  },
  vvip: {
    color: '#DC2626',
    bgColor: '#FEE2E2',
    icon: '👑',
  },
  admin: {
    color: '#7C3AED',
    bgColor: '#EDE9FE',
    icon: '🛡️',
  },
};

export function VIPBadge({
  level,
  code,
  size = 'medium',
  showText = true,
  className = '',
}: VIPBadgeProps) {
  // 如果没有提供code,根据level推断
  const levelCode: VIPLevelCode = code || (level === VIPLevel.FREE ? 'free' : level === VIPLevel.VIP ? 'vip' : level === VIPLevel.VVIP ? 'vvip' : 'admin');
  const config = VIP_CONFIGS[levelCode];

  const sizeStyles = {
    small: 'text-xs px-2 py-0.5',
    medium: 'text-sm px-3 py-1',
    large: 'text-base px-4 py-1.5',
  };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeStyles[size]} ${className}`}
      style={{
        color: config.color,
        backgroundColor: config.bgColor,
      }}
    >
      <span>{config.icon}</span>
      {showText && (
        <span>
          {level === VIPLevel.FREE ? '免费' : level === VIPLevel.VIP ? 'VIP' : level === VIPLevel.VVIP ? 'VVIP' : '管理员'}
        </span>
      )}
    </span>
  );
}

/**
 * VIP卡片组件
 * 显示VIP信息卡片
 */
interface VIPCardProps {
  level: VIPLevel;
  name: string;
  expireAt: string | null;
  daysRemaining: number | null;
  className?: string;
}

export function VIPCard({ level, name, expireAt, daysRemaining, className = '' }: VIPCardProps) {
  const levelCode: VIPLevelCode = level === VIPLevel.FREE ? 'free' : level === VIPLevel.VIP ? 'vip' : level === VIPLevel.VVIP ? 'vvip' : 'admin';
  const config = VIP_CONFIGS[levelCode];

  return (
    <div
      className={`rounded-lg border-2 p-4 ${className}`}
      style={{
        borderColor: config.color,
        backgroundColor: config.bgColor,
      }}
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl">{config.icon}</span>
        <div className="flex-1">
          <div className="font-semibold" style={{ color: config.color }}>
            {name}
          </div>
          {expireAt && (
            <div className="mt-1 text-sm text-gray-600">
              {daysRemaining !== null ? (
                <span>剩余 {daysRemaining} 天</span>
              ) : (
                <span>永久有效</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
